/**
 * `<AnalysisSkippedState>` — empty state shown when the recruiter has
 * explicitly skipped an interview analysis.
 *
 * Phase 2.6 — interview-prep-spec.md §7.5.
 *
 * "Add it now →" affordance re-opens the paste-box (parent flips state).
 *
 * Cofounder ruling 3 (Phase 2.6): the downstream prep generated WITHOUT
 * this analysis is silent. This component states the consequence clearly
 * ("the next stage's prep was generated without this analysis") but does
 * NOT surface a warn-banner / failure-banner — that's the canonical
 * silent behaviour per spec §2.4.
 */
import type { JSX } from "react";
import { SkipForward } from "lucide-react";
import { Button } from "@/components/common/Button";

interface AnalysisSkippedStateProps {
  stageName: string;
  onAddItNow: () => void;
}

export function AnalysisSkippedState({
  stageName,
  onAddItNow,
}: AnalysisSkippedStateProps): JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-content-px py-6 text-center"
      data-testid="analysis-skipped"
    >
      <SkipForward
        size={32}
        className="mb-4 text-text-tertiary"
        aria-hidden
      />
      <h3 className="mb-1.5 text-base font-semibold text-text-primary">
        You skipped the {stageName} analysis.
      </h3>
      <p className="mb-5 max-w-md text-sm text-text-secondary">
        The next stage&apos;s prep was generated without this analysis.
      </p>
      <Button
        variant="secondary"
        onClick={onAddItNow}
        data-testid="analysis-skipped-add"
      >
        Add it now
      </Button>
    </div>
  );
}
